import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { motion, AnimatePresence } from 'framer-motion';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import InputAdornment from '@mui/material/InputAdornment';
import axios from 'axios';
import config from '../config';

// ---- Styles ---------------------------------------------------------------
const useStyles = makeStyles(() => ({
  page: {
    minHeight: '100vh',
    minHeight: '100dvh', // Dynamic viewport height for mobile
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    boxSizing: 'border-box',
  },
  card: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    minHeight: '100dvh', // Dynamic viewport height for mobile
    borderRadius: 0, // Remove border radius for full screen
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    height: 127,
    background: 'linear-gradient(180deg, #724F96 0%, #C084FC 100%)',
  },
  headerSoft: {
    height: 127,
    background: 'linear-gradient(180deg, #C084FC 50%, #F7EFFF 100%)',
  },
  content: {
    padding: '20px 24px 28px',
  },
  titleWrap: {
    marginTop: 12,
    marginBottom: 12,
  },
  hello: {
    fontWeight: 700,
    fontSize: 30.26,
    lineHeight: '30.75px',
    color: '#C084FC',
  },
  subtitle: {
    fontWeight: 400,
    fontSize: 30.26,
    lineHeight: '30.75px',
    color: '#C084FC',
  },
  subText: {
    color: '#B6B6B6',
    fontSize: 18,
    fontFamily: 'Poppins',
    fontWeight: 500,
    marginBottom: 12,
  },
  formRow: {
    marginTop: 14,
  },
  outlined: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
    },
  },
  // Buttons (deactivated/activated)
  cta: {
    height: 51,
    borderRadius: 10.4,
    textTransform: 'none',
    fontWeight: 700,
    color: '#fff',
    boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
    background: 'linear-gradient(90deg, rgba(192, 132, 252, 0.50) 0%, rgba(114, 79, 150, 0.50) 100%)',
    '&.active': {
      background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
    },
  },
  linkRow: {
    marginTop: 14,
    textAlign: 'center',
  },
  muted: { color: '#B6B6B6' },
  accent: { color: '#C084FC', textDecoration: 'none' },
  // OTP boxes
  otpBox: {
    width: 48,
    height: 56,
    borderRadius: 16,
    border: '1.6px #DDDDDD solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: '#757575',
    outline: 'none',
  },
  otpBoxActive: {
    border: '1.8px #C084FC solid',
  },
  resend: {
    color: '#009DFF',
    fontSize: 16,
    fontFamily: 'Poppins',
    fontWeight: 500,
    marginTop: 16,
    cursor: 'pointer',
    textAlign: 'center',
  },
  // Success icon container (62x62 gradient square per figma)
  successIconWrap: {
    width: 62,
    height: 62,
    borderRadius: 12,
    background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
}));

// ---- Component ------------------------------------------------------------
export default function SignUpFlow() {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();

  // flow: details -> otp -> password -> success
  const [step, setStep] = useState('details');

  // shared snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // 1) DETAILS FORM (per Figma v1)
  const [salutation, setSalutation] = useState('Ms.');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // phone number only
  const [email, setEmail] = useState(''); // optional email for account

  const isValidMobile = (val) => /^\+?\d{10,15}$/.test(String(val));
  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val).toLowerCase());
  const isValidDate = (val) => {
    if (!val) return false;
    const date = new Date(val);
    const today = new Date();
    const minDate = new Date('1900-01-01');
    
    // Check if date is valid and within reasonable range
    return date instanceof Date && 
           !isNaN(date) && 
           date >= minDate && 
           date <= today &&
           val.length === 10 && // YYYY-MM-DD format
           /^\d{4}-\d{2}-\d{2}$/.test(val);
  };

  const detailsValid = Boolean(
    salutation && firstName.trim() && lastName.trim() && isValidMobile(phoneNumber) && (!dob || isValidDate(dob))
  );

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!detailsValid) return;
    
    try {
      // First check if user already exists
      const checkUserResponse = await axios.post(`${config.API_BASE_URL}/check-user-exists`, { 
        phone: phoneNumber 
      });
      
      if (checkUserResponse.data.exists) {
        showSnackbar('An account with this phone number already exists. Please sign in instead.', 'error');
        return;
      }
      
      // If user doesn't exist, proceed with OTP generation
      const response = await axios.post(`${config.API_BASE_URL}/generate-otp`, { phone: phoneNumber });
      if (response.status === 200) {
        showSnackbar('OTP sent successfully to your phone!', 'success');
        setStep('otp');
      }
    } catch (err) {
      console.error('Signup error:', err);
      // Handle specific error cases
      if (err.response?.status === 400 && err.response?.data?.error?.includes('already exists')) {
        showSnackbar('An account with this phone number already exists. Please sign in instead.', 'error');
      } else if (err.response?.data?.message?.includes('already exists')) {
        showSnackbar('An account with this phone number already exists. Please sign in instead.', 'error');
      } else {
        showSnackbar(err.response?.data?.message || 'Failed to send OTP. Please try again.', 'error');
      }
    }
  };

  // 2) OTP FORM (per Figma v2)
  const [otp, setOtp] = useState(['', '', '', '', '', '']); // 6 digits
  const inputsRef = useRef([]);
  const otpValid = otp.every((d) => d !== '');
  const otpValue = useMemo(() => otp.join(''), [otp]);

  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const next = [...otp];
      next[index] = value;
      setOtp(next);
      if (value && inputsRef.current[index + 1]) inputsRef.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && inputsRef.current[index - 1]) {
      const next = [...otp];
      next[index - 1] = '';
      setOtp(next);
      inputsRef.current[index - 1].focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otpValid) return;
    try {
      const response = await axios.post(`${config.API_BASE_URL}/verify-otp-reset`, { phone: phoneNumber, otp: parseInt(otpValue) });
      if (response.status === 200) {
        showSnackbar('OTP verified successfully!', 'success');
        setStep('password');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      showSnackbar(err.response?.data?.message || 'Invalid or expired OTP. Try again.', 'error');
    }
  };

  const resendOtp = async () => {
    try {
      const response = await axios.post(`${config.API_BASE_URL}/generate-otp`, { phone: phoneNumber });
      if (response.status === 200) showSnackbar('OTP resent successfully!', 'success');
    } catch (err) {
      console.error('OTP resend error:', err);
      showSnackbar(err.response?.data?.message || 'Could not resend OTP', 'error');
    }
  };

  // 3) PASSWORD FORM (per Figma v3)
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const passwordValid = useMemo(() => {
    if (password.length < 6) return false;
    return password === confirmPassword;
  }, [password, confirmPassword]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!passwordValid) return;

    const name = `${salutation} ${firstName} ${lastName}`.trim();

    const payload = {
      name,
      email: email || '',
      phone: phoneNumber,
      role: 'customer', // align with DB enum
      password,
      gender: gender || null, // Send null if empty
      dob: dob || null, // Send null if empty
      salutation,
      first_name: firstName,
      last_name: lastName,
    };

    try {
      const response = await axios.post(`${config.API_BASE_URL}/signup`, payload);
      if (response.status === 201 || response.status === 200) {
        showSnackbar('Signup successful!', 'success');
        setStep('success');
      }
    } catch (err) {
      console.error('Signup error:', err);
      showSnackbar(err.response?.data?.message || 'An error occurred during sign-up.', 'error');
    }
  };

  const goToLogin = () => navigate('/login');

  // 4) SUCCESS SCREEN (per Figma)
  const SuccessScreen = (
    <Paper className={classes.card} component={motion.div} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Box className={classes.content}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 1 }}>
          <div className={classes.successIconWrap}>
            <CheckRoundedIcon sx={{ color: '#fff', fontSize: 34 }} />
          </div>
        </Box>
        <Typography align="center" sx={{ color: '#C084FC', fontSize: 22, fontFamily: 'Inter', fontWeight: 700, mt: 2 }}>
          Success!
        </Typography>
        <Typography align="center" sx={{ color: '#B6B6B6', fontSize: 18, fontFamily: 'Poppins', fontWeight: 500, mt: 1, px: 2 }}>
          Congratulations! You have successfully signed up. Sign in to continue.
        </Typography>

        <Box mt={3}>
          <Button
            fullWidth
            className={`${classes.cta} active`}
            variant="contained"
            onClick={goToLogin}
          >
            Sign in
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  // ---- Renders ------------------------------------------------------------
  const DetailsScreen = (
    <Paper className={classes.card} component={motion.div} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Box className={classes.header} />
      <Box className={classes.content}>
        <Box className={classes.titleWrap}>
          <Typography className={classes.hello}>Hello!</Typography>
          <Typography className={classes.subtitle}>Signup to <br/>get started</Typography>
        </Box>

        <Box component="form" onSubmit={handleDetailsSubmit} noValidate>
          <Grid container spacing={1.5} className={classes.formRow} alignItems="center">
            <Grid item xs={4} sm={4} md={4}>
              <FormControl fullWidth className={classes.outlined} size="small">
                <InputLabel id="salutation-label">Title *</InputLabel>
                <Select
                  labelId="salutation-label"
                  label="Title *"
                  value={salutation}
                  onChange={(e) => setSalutation(e.target.value)}
                >
                  {['Ms.', 'Mr.', 'Mx.', 'Dr.'].map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8} sm={8} md={8}>
              <TextField
                fullWidth
                label="Last name *"
                size="small"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={classes.outlined}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="First name *"
                size="small"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={classes.outlined}
              />
            </Grid>
          </Grid>

          <Grid container spacing={1.5} className={classes.formRow}>
            <Grid item xs={12}>
              <FormControl fullWidth className={classes.outlined} size="small">
                <InputLabel id="gender-label">Gender (Optional)</InputLabel>
                <Select
                  labelId="gender-label"
                  label="Gender (Optional)"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  {['Female', 'Male', 'Non-binary', 'Prefer not to say'].map((g) => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date of Birth (Optional)"
                type="date"
                size="small"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                className={classes.outlined}
                InputLabelProps={{ shrink: true }}
                error={Boolean(dob) && !isValidDate(dob)}
                helperText={Boolean(dob) && !isValidDate(dob) ? 'Please enter a valid date (YYYY-MM-DD format)' : 'Optional - Leave blank if you prefer not to provide'}
                inputProps={{
                  max: new Date().toISOString().split('T')[0], // Today's date
                  min: '1900-01-01' // Minimum date
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarMonthIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number *"
                placeholder="+91XXXXXXXXXX"
                size="small"
                value={phoneNumber}
                onChange={(e) => {
                  // Validator: /^\+?\d{10,15}$/ — allow an optional leading
                  // '+' and digits only; strip anything else as the user
                  // types so letters never appear in the field.
                  let v = e.target.value.replace(/[^\d+]/g, '');
                  v = v.replace(/(?!^)\+/g, ''); // only a leading '+'
                  setPhoneNumber(v.slice(0, 16));
                }}
                inputProps={{ inputMode: 'tel', maxLength: 16 }}
                className={classes.outlined}
                error={Boolean(phoneNumber) && !isValidMobile(phoneNumber)}
                helperText={Boolean(phoneNumber) && !isValidMobile(phoneNumber) ? 'Please enter a valid phone number' : ' '}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email (Optional)"
                placeholder="name@domain.com"
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={classes.outlined}
                error={Boolean(email) && !isValidEmail(email)}
                helperText={Boolean(email) && !isValidEmail(email) ? 'Please enter a valid email address' : ' '}
              />
            </Grid>
          </Grid>

          <Box mt={2.5}>
            <Button
              fullWidth
              className={`${classes.cta} ${detailsValid ? 'active' : ''}`}
              variant="contained"
              type="submit"
              disabled={!detailsValid}
            >
              Verify Phone Number
            </Button>
          </Box>

          <Box className={classes.linkRow}>
            <Typography variant="body2" component="span" className={classes.muted}>
              Already have an account?{' '}
            </Typography>
            <Link to="/login" className={classes.accent}>Sign In</Link>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  const OtpScreen = (
    <Paper className={classes.card} component={motion.div} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Box className={classes.content}>
        <Typography style={{ color: '#C084FC', fontSize: 22, fontFamily: 'Poppins', fontWeight: 700 }}>Verification Code</Typography>
        <Typography className={classes.subText}>
          Verification code sent to {phoneNumber}. This will be used as your login id.
        </Typography>

        <Box component="form" onSubmit={handleOtpSubmit}>
          <Grid container spacing={1.5} justifyContent="center" sx={{ mt: 2 }}>
            {otp.map((digit, idx) => (
              <Grid item key={idx}>
                <input
                  ref={(el) => (inputsRef.current[idx] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                  className={`${classes.otpBox} ${digit ? classes.otpBoxActive : ''}`}
                  style={{ textAlign: 'center' }}
                />
              </Grid>
            ))}
          </Grid>

          <div className={classes.resend} onClick={resendOtp}>Resend OTP?</div>

          <Box mt={3}>
            <Button
              fullWidth
              className={`${classes.cta} ${otpValid ? 'active' : ''}`}
              variant="contained"
              type="submit"
              disabled={!otpValid}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  const PasswordScreen = (
    <Paper className={classes.card} component={motion.div} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Box className={classes.headerSoft} />
      <Box className={classes.content}>
        <Box className={classes.titleWrap}>
          <Typography className={classes.hello}>Hello!</Typography>
          <Typography className={classes.subtitle}>Signup to <br/>get started</Typography>
        </Box>

        <Grid container spacing={1.5} className={classes.formRow}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Create Password"
              type="password"
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={classes.outlined}
              helperText="Password must be at least 6 characters long"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              size="small"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={classes.outlined}
              error={Boolean(confirmPassword) && password !== confirmPassword}
              helperText={Boolean(confirmPassword) && password !== confirmPassword ? 'Passwords do not match' : ' '}
            />
          </Grid>
        </Grid>

        <Box mt={2.5}>
          <Button
            fullWidth
            className={`${classes.cta} ${passwordValid ? 'active' : ''}`}
            variant="contained"
            onClick={handleSignUp}
            disabled={!passwordValid}
          >
            Sign up
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <div className={classes.page}>
      <AnimatePresence mode="wait">
        {step === 'details' && (
          <motion.div key="details" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            {DetailsScreen}
          </motion.div>
        )}
        {step === 'otp' && (
          <motion.div key="otp" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            {OtpScreen}
          </motion.div>
        )}
        {step === 'password' && (
          <motion.div key="password" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            {PasswordScreen}
          </motion.div>
        )}
        {step === 'success' && (
          <motion.div key="success" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
            {SuccessScreen}
          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
